<template>
  <div class="searchable-dropdown" ref="dropdownContainer">
    <!-- Multi-select chips display -->
    <div v-if="mode === 'multi' && dropdown.selectedItems.length" class="selected-chips mb-2">
      <span 
        v-for="(item, index) in dropdown.selectedItems" 
        :key="index"
        class="badge badge-primary mr-1 mb-1">
        {{ dropdown.getItemText(item) }}
        <i class="fas fa-times ml-1" @click="() => dropdown.remove(item)" style="cursor: pointer;"></i>
      </span>
    </div>

    <!-- Search input -->
    <div class="position-relative">
      <input
        ref="searchInput"
        type="text"
        class="form-control form-control-sm"
        :placeholder="placeholder"
        v-model="dropdown.search"
        @input="() => dropdown.onInput()"
        @focus="() => dropdown.open()"
        @blur="() => dropdown.onBlur()"
        @keydown="(e) => dropdown.handleKeydown(e)"
        :aria-expanded="dropdown.isOpen"
        :aria-autocomplete="'list'"
        :disabled="disabled"
        role="combobox"
      />
      <i 
        v-if="dropdown.hasSelection && !disabled"
        class="fas fa-times clear-icon"
        @click="() => dropdown.clear()"
        title="Clear selection"
      ></i>
    </div>

    <!-- Dropdown list -->
    <div 
      v-show="dropdown.isOpen && dropdown.filteredItems.length"
      class="dropdown-list"
      role="listbox">
      <div
        v-for="(item, index) in dropdown.filteredItems"
        :key="index"
        class="dropdown-item"
        :class="{ 'selected': dropdown.isSelected(item) }"
        @click="() => dropdown.select(item)"
        :aria-selected="dropdown.isSelected(item)"
        role="option">
        <i v-if="mode === 'multi' && dropdown.isSelected(item)" class="fas fa-check mr-2"></i>
        {{ dropdown.getItemText(item) }}
      </div>
    </div>

    <!-- No results message -->
    <div 
      v-show="dropdown.isOpen && !dropdown.filteredItems.length && dropdown.search"
      class="dropdown-list no-results">
      <div class="dropdown-item disabled">No results found</div>
    </div>
  </div>
</template>

<style scoped>
.searchable-dropdown {
  position: relative;
}

.selected-chips {
  display: flex;
  flex-wrap: wrap;
}

.selected-chips .badge {
  font-size: 0.875rem;
  padding: 0.35rem 0.5rem;
  display: inline-flex;
  align-items: center;
}

.position-relative {
  position: relative;
}

.clear-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #6c757d;
  font-size: 14px;
  z-index: 1;
}

.clear-icon:hover {
  color: #495057;
}

.dropdown-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 250px;
  overflow-y: auto;
  background: white;
  border: 1px solid #ced4da;
  border-top: none;
  border-radius: 0 0 0.25rem 0.25rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  margin: 0;
}

.dropdown-list.no-results {
  max-height: auto;
}

.dropdown-item {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  color: #212529;
  font-size: 0.875rem;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
  transition: background-color 0.15s ease-in-out;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

.dropdown-item.selected {
  background-color: #e9ecef;
  font-weight: 500;
}

.dropdown-item.disabled {
  cursor: default;
  color: #6c757d;
  font-style: italic;
}

.dropdown-item.disabled:hover {
  background-color: transparent;
}

/* Scrollbar styling */
.dropdown-list::-webkit-scrollbar {
  width: 8px;
}

.dropdown-list::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.dropdown-list::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.dropdown-list::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>

<script>
export default {
  props: {
    o: { 
      type: Object,
      required: true
    },
    p: { 
      type: String,
      required: true
    },
    items: { 
      type: Array,
      default: () => []
    },
    itemText: { 
      type: String,
      default: () => 'text'
    },
    itemValue: { 
      type: String,
      default: () => 'value'
    },
    mode: { 
      type: String,
      default: () => 'single',
      validator: (value) => ['single', 'multi'].includes(value)
    },
    placeholder: { 
      type: String,
      default: () => 'Search...'
    },
    blip: { 
      type: Number,
      default: () => 4e2
    },
    disabled: {
      type: Boolean,
      default: () => false
    }
  },
  data() {
    return {
      dropdown: null
    };
  },
  created() {
    var { o, p, itemText, itemValue, mode, blip } = this;
    var vm = this;
    
    var dropdown = {
      search: '',
      isOpen: false,
      filteredItems: [],
      selectedItems: [],
      timeout: null,

      getItemText(item) {
        if (typeof item === 'string') return item;
        var val = item[itemText];
        return val != null ? String(val) : '';
      },

      getItemValue(item) {
        if (typeof item === 'string') return item;
        return item[itemValue] !== undefined ? item[itemValue] : item;
      },

      get hasSelection() {
        if (mode === 'multi') {
          return this.selectedItems.length > 0;
        }
        return o[p] !== null && o[p] !== undefined && o[p] !== '';
      },

      filter() {
        var searchLower = this.search.toLowerCase().trim();
        var items = vm.items; // Reference items from component instance
        this.filteredItems = !searchLower 
          ? items.slice() 
          : items.filter(item => {
              var text = this.getItemText(item).toLowerCase();
              return text.includes(searchLower);
            });
        
        vm.$emit('search', {
          query: this.search,
          filteredCount: this.filteredItems.length
        });
      },

      onInput() {
        clearTimeout(this.timeout);
        this.timeout = setTimeout(() => {
          this.filter();
          // Auto-open dropdown when user starts typing
          if (!this.isOpen && this.search) {
            this.isOpen = true;
          }
        }, blip);
      },

      onBlur() {
        // Small delay to allow click events on dropdown items to fire first
        setTimeout(() => {
          this.close();
        }, 150);
      },

      open() {
        this.isOpen = true;
        if (mode === 'single') {
          // Select all text on focus so user can start typing to search
          vm.$nextTick(() => {
            if (vm.$refs.searchInput) {
              vm.$refs.searchInput.select();
            }
          });
        }
        if (!this.search) {
          this.filter();
        }
      },

      close() {
        this.isOpen = false;
        if (mode !== 'single') return;
        
        var currentValue = o[p];
        if (!currentValue) {
          this.search = '';
          return;
        }
        
        var selectedItem = vm.items.find(item => this.getItemValue(item) === currentValue);
        if (!selectedItem) {
          this.search = '';
          o[p] = null;
        } else {
          this.search = this.getItemText(selectedItem);
        }
      },

      isSelected(item) {
        if (mode === 'single') {
          return this.getItemValue(item) === o[p];
        }
        var itemValue = this.getItemValue(item);
        return this.selectedItems.some(selected => 
          this.getItemValue(selected) === itemValue
        );
      },

      selectSingle(item) {
        var selectedValue = this.getItemValue(item);
        o[p] = selectedValue;
        this.search = this.getItemText(item);
        this.close();
        vm.$emit('selected', item);
        vm.$emit('input', selectedValue);
      },

      selectMulti(item) {
        if (this.isSelected(item)) {
          this.remove(item);
        } else {
          this.selectedItems.push(item);
          this.updateMultiValue();
        }
        this.search = '';
        this.filter();
        vm.$refs.searchInput.focus();
      },

      select(item) {
        // Use a concise ternary expression instead of an if/else block
        mode === 'single' ? this.selectSingle(item) : this.selectMulti(item);
      },

      remove(item) {
        if (mode !== 'multi') return;
        
        var itemValue = this.getItemValue(item);
        var index = this.selectedItems.findIndex(selected => 
          this.getItemValue(selected) === itemValue
        );
        
        if (index > -1) {
          this.selectedItems.splice(index, 1);
          this.updateMultiValue();
        }
      },

      updateMultiValue() {
        if (mode !== 'multi') return;
        
        o[p] = this.selectedItems.map(item => this.getItemValue(item));
        vm.$emit('selected', this.selectedItems);
        vm.$emit('input', o[p]);
      },

      clearSingle() {
        o[p] = null;
        vm.$emit('input', null);
      },

      clearMulti() {
        this.selectedItems = [];
        o[p] = [];
        vm.$emit('input', []);
      },

      clear() {
        mode === 'single' ? this.clearSingle() : this.clearMulti();
        this.search = '';
        this.filter();
        this.close();
        vm.$emit('cleared');
      },

      getDisplayText(value) {
        if (mode !== 'single' || !value) return '';
        
        var selectedItem = vm.items.find(item => this.getItemValue(item) === value);
        return selectedItem ? this.getItemText(selectedItem) : '';
      },

      handleKeydown(event) {
        if (!this.isOpen && (event.key === 'Enter' || event.key === ' ')) {
          this.open();
          event.preventDefault();
          return;
        }

        if (!this.isOpen) return;

        switch (event.key) {
          case 'Enter':
            event.preventDefault();
            if (this.filteredItems.length > 0) {
              this.select(this.filteredItems[0]);
            } else {
              // No items to select, just close and validate
              this.close();
              vm.$refs.searchInput.blur();
            }
            break;

          case 'Escape':
            event.preventDefault();
            this.search = this.getDisplayText(o[p]);
            this.close();
            break;

          case 'Tab':
            this.close();
            break;
        }
      },

      handleClickOutside(event) {
        var container = vm.$refs.dropdownContainer;
        if (container && !container.contains(event.target)) {
          this.close();
        }
      },

      initMultiSelect() {
        var existingValues = o[p];
        if (Array.isArray(existingValues) && existingValues.length > 0) {
          this.selectedItems = vm.items.filter(item => 
            existingValues.includes(this.getItemValue(item))
          );
        } else {
          o[p] = [];
        }
      },

      initSingleSelect() {
        var currentValue = o[p];
        if (!currentValue) return;
        
        var selectedItem = vm.items.find(item => 
          this.getItemValue(item) === currentValue
        );
        if (!selectedItem) {
          this.search = '';
          o[p] = null;
        } else {
          this.search = this.getItemText(selectedItem);
        }
      },

      init() {
        mode === 'multi' ? this.initMultiSelect() : this.initSingleSelect();
        this.filter();
        return this;
      }
    };

    this.dropdown = dropdown.init();
  },
  mounted() {
    document.addEventListener('click', this.dropdown.handleClickOutside);
  },
  watch: {
    items: {
      handler() {
        // Refilter when items change
        this.dropdown.filter();
      },
      deep: true
    }
  },
  beforeDestroy() {
    document.removeEventListener('click', this.dropdown.handleClickOutside);
    clearTimeout(this.dropdown.timeout);
  }
};
</script>
